import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flex } from '@fabric-ui/theme';
import { emptyObject } from '@fabric-ui/utils';
import { Box, Button } from '@mui/material';
import { getParamFromURL } from '@views/CustomDashboards/components/Clickthrough/utils';
import { useSnackbar } from 'notistack';
import SavedViewPopper, { useSaveView } from '../../components/SavedViewPopper';
import SavedViewSelect from '../../components/SavedViewSelect';
import { useAvContext } from '../../context/AvContextProvider';
import { useGetUsersInfo } from '../../hooks/useGetUsersInfo';
import { Permission, PermissionEntitiesNames } from '../../types';
import { ScreenType } from '../../types/savedViews.types';
import { normalizeBooleanParam } from '../../utils/filterUtils';
import { useUserViews } from '../Tickets/hooks';
import { defaultFiltersForEntity } from './Entities.constants';

export const isDefaultActiveView = view => view.global && view.name === 'Active';

export const getDefaultView = (views: any[], shouldSearchActiveView = true) =>
  views.find(v => v.defaultView || v.accountDefaultView) ||
  (shouldSearchActiveView ? views.find(v => isDefaultActiveView(v)) : undefined) ||
  {};

export const useSavedViews = ({
  viewScreenType,
  filters,
  setFilters,
  defaultFilters = {},
  activeProjName,
  controlledSelectedViewChanged = false,
  setControlledSelectedViewChanged,
  defaultEnabled = true,
  size = 'small',
  showSaveSuccessSnackbar = true,
  customProps = {
    saveViewPopperStyle: emptyObject,
    saveViewMessage: undefined,
    customAccountDefaultTooltip: 'as Account Default View',
    shouldEnableUpdatingSelectedView: (selectedView, user) => !selectedView?.global && selectedView?.createdByUserId === user.userId,
    getViewConfigForSaving: () => filters,
    getCustomDefaultView: getDefaultView,
    getCustomViewsList: views => views,
    shouldDisplayDefaultViewButton: () => true,
    shouldDisplayFavoriteButton: () => true,
    enableViewsRequest: true,
    getCustomDefaultFiltersForEntity: undefined,
  },
}: {
  viewScreenType: ScreenType;
  filters: any;
  setFilters: any;
  defaultFilters?: any;
  activeProjName?: string;
  controlledSelectedViewChanged?: boolean;
  setControlledSelectedViewChanged?: any;
  defaultEnabled?: boolean;
  size?: 'xSmall' | 'small' | 'medium';
  showSaveSuccessSnackbar?: boolean;
  customProps?: {
    saveViewPopperStyle?: object;
    saveViewMessage?: React.ReactNode;
    customAccountDefaultTooltip?: string;
    customDefaultTooltip?: string;
    shouldEnableUpdatingSelectedView?: (selectedView: any, user: any) => boolean;
    getViewConfigForSaving?: () => any;
    getCustomDefaultView?: (views: any[]) => any;
    getCustomViewsList?: (views: any[], isLoading: boolean) => any[];
    shouldDisplayDefaultViewButton?: (view) => boolean;
    shouldDisplayFavoriteButton?: (view) => boolean;
    enableViewsRequest?: boolean;
    getCustomDefaultFiltersForEntity?: (projName: string) => { [key: string]: string[] };
    skipAutoDefaultView?: boolean;
    viewModified?: boolean;
    getFiltersOnClearView?: () => Record<string, unknown>;
  };
}) => {
  const {
    user,
    userPermissions: { hasAllowedPermissionToResource },
    accountEntities: { aggProjs, fieldMap },
  } = useAvContext();
  const saveView = useSaveView();
  const { enqueueSnackbar } = useSnackbar();
  const selectViewRef = useRef<Element>(undefined);
  const saveAsViewButtonRef = useRef<Element>(undefined);

  const [openEditViewPopper, setOpenEditViewPopper] = useState(false);
  const [openSaveViewPopper, setOpenSaveViewPopper] = useState(false);
  const [selectedViewChanged, setSelectedViewChanged] = useState(false);

  const {
    saveViewPopperStyle = emptyObject,
    saveViewMessage,
    customAccountDefaultTooltip = 'as Account Default View',
    customDefaultTooltip = 'My Default View',
    shouldEnableUpdatingSelectedView = (selectedView, user) => !selectedView?.global && selectedView?.createdByUserId === user.userId,
    getViewConfigForSaving = () => filters,
    getCustomDefaultView = getDefaultView,
    getCustomViewsList = views => views,
    shouldDisplayDefaultViewButton = () => true,
    shouldDisplayFavoriteButton = () => true,
    enableViewsRequest = true,
    getCustomDefaultFiltersForEntity,
  } = customProps;

  const {
    userViews = [],
    refetch: viewsRefetch,
    isLoading,
  } = useUserViews({
    screenType: viewScreenType,
    proj: activeProjName && aggProjs[activeProjName] ? aggProjs[activeProjName].projId : undefined,
    enabled: enableViewsRequest,
  });

  const viewsList = getCustomViewsList(userViews, isLoading);
  const viewsCreatorsIds = useMemo(() => viewsList.filter(v => v.createdByUserId).map(v => v.createdByUserId), [viewsList]);
  const { usersInfo } = useGetUsersInfo([...new Set([...viewsCreatorsIds, user.userId])]);

  const replaceStateProperty = (obj, stateField, newStateField) => {
    const { config, ...view } = obj;
    const { [stateField]: val, activeProjName: oldActiveProjName, ...partialConfig } = config;
    const newConfig = {
      ...partialConfig,
      ...(fieldMap[newStateField].visibilityConfig.config.hidden ? {} : { [newStateField]: val }),
      ...(activeProjName ? { activeEntityId: aggProjs[activeProjName].entityTypeId.name } : {}),
    };
    return { ...view, config: newConfig };
  };
  const customActiveView = useMemo(
    () =>
      viewsList.map(v =>
        v.global && v.name === 'Active' ? replaceStateProperty(v, 'finding.state', `${aggProjs[activeProjName!].pathAlias}.state`) : v
      ),
    [viewsList, activeProjName]
  );

  const viewIdToViewInfo = useMemo(
    () =>
      customActiveView.reduce(
        (obj, view) => ({
          ...obj,
          [view.id]: {
            ...view,
            config: {
              ...view.config,
              ...(view.config?.investigationMode !== undefined && {
                investigationMode: normalizeBooleanParam(getParamFromURL(view.config.investigationMode)),
              }),
              viewId: [view.id],
            },
          },
        }),
        {}
      ),
    [customActiveView]
  );

  const defaultView = useMemo(() => getCustomDefaultView(customActiveView), [customActiveView]);

  const selectedViewIdArray = customProps.viewModified
    ? []
    : filters.viewId?.length
      ? filters.viewId
      : customProps.skipAutoDefaultView
        ? []
        : [defaultView.id];

  const selectedView = viewIdToViewInfo[selectedViewIdArray[0]] ?? viewIdToViewInfo[filters.viewId?.[0]];

  const getDefaultFiltersForEntity = defaultFiltersForEntity(aggProjs);
  const getMergedDefaultFilters = () => ({
    ...(activeProjName
      ? getCustomDefaultFiltersForEntity
        ? getCustomDefaultFiltersForEntity(activeProjName)
        : getDefaultFiltersForEntity(activeProjName)
      : {}),
    ...defaultFilters,
  });

  const setNewView = ({ id, config }) => setFilters({ ...getMergedDefaultFilters(), ...config, viewId: [id] });

  const onChangeView = viewId => {
    setSelectedViewChanged(false);
    setControlledSelectedViewChanged?.(false);
    if (viewId) {
      if (viewIdToViewInfo[viewId]) {
        setNewView(viewIdToViewInfo[viewId]);
      }
    } else {
      setFilters({
        ...getMergedDefaultFilters(),
        ...(customProps.getFiltersOnClearView?.() ?? {}),
        viewId: [],
        ...(activeProjName ? { activeEntityId: aggProjs[activeProjName].entityTypeId.name } : {}),
      });
    }
  };

  const onSaveViewSuccess = view => {
    viewsRefetch().then(() => setNewView(view));
    if (showSaveSuccessSnackbar) {
      enqueueSnackbar('View Saved Successfully', { variant: 'success' });
    }
    setSelectedViewChanged(false);
    setControlledSelectedViewChanged?.(false);
  };

  const getDefaultViewByEntity = newProjName => {
    const customActiveView = viewsList.map(v =>
      v.global && v.name === 'Active' ? replaceStateProperty(v, 'finding.state', `${aggProjs[newProjName!].pathAlias}.state`) : v
    );
    return getDefaultView(customActiveView);
  };

  const onSaveView = () => {
    saveView(
      {
        ...viewIdToViewInfo[filters.viewId[0]],
        config: getViewConfigForSaving?.(),
        ...(activeProjName && aggProjs[activeProjName] && { projectionId: aggProjs[activeProjName].projId }),
      },
      onSaveViewSuccess,
      true
    );
  };

  const onDeleteView = () => viewsRefetch().then(() => setNewView(defaultView));

  const hasAppliedDefaultViewRef = useRef<string | null>(null);

  const structuralFilterKeys = useMemo(() => new Set(['viewId', 'headCells', ...Object.keys(defaultFilters)]), [defaultFilters]);

  const hasExternalUrlFilters = useMemo(
    () => Object.entries(filters).some(([key, value]) => !structuralFilterKeys.has(key) && Array.isArray(value) && value.length > 0),
    [filters, structuralFilterKeys]
  );

  useEffect(() => {
    if (hasAppliedDefaultViewRef.current === activeProjName) return;
    const candidateView = viewIdToViewInfo[defaultView?.id];
    const viewBelongsToCurrentEntity =
      !candidateView?.config?.activeEntityId || candidateView.config.activeEntityId[0] === filters.activeEntityId?.[0];
    const shouldSetToDefaultView =
      !filters.viewId?.length &&
      !hasExternalUrlFilters &&
      defaultView?.id &&
      candidateView &&
      viewBelongsToCurrentEntity &&
      Object.keys(viewIdToViewInfo).length;
    if (shouldSetToDefaultView) {
      hasAppliedDefaultViewRef.current = activeProjName ?? null;
      if (!customProps.skipAutoDefaultView) {
        setNewView(candidateView);
      }
    }
  }, [viewIdToViewInfo, hasExternalUrlFilters]);

  const SelectedView = hasAllowedPermissionToResource({ resource: PermissionEntitiesNames.VIEWS, permission: Permission.READ }) && (
    <Box ref={selectViewRef} sx={{ '> .MuiButtonBase-root:first-of-type': { ...flex.justifyBetween, width: 250 } }}>
      <SavedViewSelect
        selectedViewId={selectedViewIdArray[0]}
        onChange={onChangeView}
        options={viewsList}
        userToDisplayName={usersInfo}
        viewsRefetch={viewsRefetch}
        onDeleteView={onDeleteView}
        openViewPopper={() => setOpenEditViewPopper(true)}
        defaultEnabled={defaultEnabled}
        size={size}
        shouldDisplayDefaultViewButton={shouldDisplayDefaultViewButton}
        shouldDisplayFavoriteButton={shouldDisplayFavoriteButton}
        customAccountDefaultTooltip={customAccountDefaultTooltip}
        customDefaultTooltip={customDefaultTooltip}
        activeProjName={activeProjName}
      />
      <SavedViewPopper
        view={selectedView || {}}
        anchorEl={selectViewRef.current}
        popperProps={{ open: openEditViewPopper, isEditMode: true }}
        handleClose={() => setOpenEditViewPopper(false)}
        refetchViews={viewsRefetch}
        screenType={viewScreenType}
        projName={activeProjName}
      />
    </Box>
  );

  const SaveView = (controlledSelectedViewChanged || selectedViewChanged) && shouldEnableUpdatingSelectedView?.(selectedView, user) && (
    <Button
      variant="outlined"
      size={size}
      sx={{ backgroundColor: 'var(--color-background-primary)' }}
      onClick={filters?.viewId?.[0] ? onSaveView : undefined}>
      Save View
    </Button>
  );

  const SaveViewButtons = hasAllowedPermissionToResource({ resource: PermissionEntitiesNames.VIEWS, permission: Permission.CREATE }) && (
    <Box sx={{ ...flex.itemsCenter, px: 1, gap: 1 }} ref={saveAsViewButtonRef}>
      {(controlledSelectedViewChanged || selectedViewChanged) && (
        <Button size={size} variant="outlined" onClick={() => setOpenSaveViewPopper(true)}>
          Save As View
        </Button>
      )}
      <SavedViewPopper
        key="save-view-popper"
        view={{ config: getViewConfigForSaving?.(), name: '', publicView: true }}
        anchorEl={saveAsViewButtonRef.current}
        popperProps={{ open: openSaveViewPopper, isEditMode: false }}
        handleClose={() => setOpenSaveViewPopper(false)}
        refetchViews={onSaveViewSuccess}
        screenType={viewScreenType}
        projName={activeProjName}
        saveViewMessage={saveViewMessage}
        saveViewPopperStyle={saveViewPopperStyle}
      />
      {SaveView}
    </Box>
  );

  return {
    selectViewRef,
    saveAsViewButtonRef,
    selectedView,
    usersInfo,
    selectedViewIdArray,
    viewsRefetch,
    userViews: customActiveView,
    isLoading,
    openEditViewPopper,
    openSaveViewPopper,
    defaultView,
    setOpenSaveViewPopper,
    setOpenEditViewPopper,
    onChangeView,
    onDeleteView,
    setNewView,
    onSaveViewSuccess,
    getDefaultViewByEntity,
    SaveViewButtons,
    SelectedView,
    SaveView,
    selectedViewChanged,
    setSelectedViewChanged,
    getDefaultFiltersForEntity,
    onSaveView,
    viewsList,
  };
};
